import React, { useEffect, useMemo, useState } from "react";
import { useAlert } from "react-alert";
import {
  AccessTimeOutlined,
  AssignmentOutlined,
  BeachAccessOutlined,
  Close,
  ErrorOutline,
  EventOutlined,
  NotificationsNoneOutlined,
  Refresh,
  Search,
} from "@material-ui/icons";
import { baseUrl } from "../../../../services/config";
import "../MonthlyStudentAttendance/parent-progress.css";
import "./school-notifications.css";
import { useParent } from "../../../../context/ParentContext";

function parseDate(value) {
  if (!value) return null;
  const date = new Date(value.replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value, includeTime = true) {
  const date = parseDate(value);
  if (!date) return "Date unavailable";
  return new Intl.DateTimeFormat("en-PK", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(includeTime ? { hour: "numeric", minute: "2-digit" } : {}),
  }).format(date);
}

function typeStyle(typeName = "General") {
  const name = typeName.toLowerCase();
  if (name.includes("vacation"))
    return { tone: "vacation", icon: BeachAccessOutlined };
  if (name.includes("calendar"))
    return { tone: "calendar", icon: EventOutlined };
  if (name.includes("attendance"))
    return { tone: "attendance", icon: AccessTimeOutlined };
  if (name.includes("test")) return { tone: "test", icon: AssignmentOutlined };
  return { tone: "general", icon: NotificationsNoneOutlined };
}

const getStudentId = (child) =>
  child?.student?.id || child?.student_id || child?.id || null;

export default function SchoolNotifications() {
  const alert = useAlert();
  const { selectedChild } = useParent();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedNotification, setSelectedNotification] = useState(null);
  const studentId = getStudentId(selectedChild);

  const loadNotifications = async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("parentToken");
      const response = await fetch(
        `${baseUrl}/notifications/parent-students/${studentId}`,
        {
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );
      const result = await response.json();
      if (!response.ok)
        throw new Error(result?.message || "Unable to load notifications.");
      const payload = result?.payload || result;
      if (!Array.isArray(payload) || !payload.length) {
        const message = "No school notifications found.";
        setNotifications([]);
        setError(message);
        alert.show(message);
        return;
      }
      const unique = Array.from(
        new Map(
          payload.map((notification) => [notification.id, notification]),
        ).values(),
      ).sort((a, b) => {
        const first = parseDate(a.date_time)?.getTime() || 0;
        const second = parseDate(b.date_time)?.getTime() || 0;
        return second - first;
      });
      setNotifications(unique);
    } catch (requestError) {
      const message = requestError.message || "Unable to load notifications.";
      setNotifications([]);
      setError(message);
      alert.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const types = useMemo(
    () =>
      Array.from(
        new Set(
          notifications
            .map((item) => item.notification_type?.name)
            .filter(Boolean),
        ),
      ),
    [notifications],
  );

  const visibleNotifications = useMemo(() => {
    const query = search.trim().toLowerCase();
    return notifications.filter((notification) => {
      const matchesType =
        typeFilter === "all" ||
        notification.notification_type?.name === typeFilter;
      const matchesSearch =
        !query ||
        String(notification.title || "")
          .toLowerCase()
          .includes(query) ||
        String(notification.description || "")
          .toLowerCase()
          .includes(query);
      return matchesType && matchesSearch;
    });
  }, [notifications, search, typeFilter]);

  return (
    <section className="parent-progress-page parent-notifications-page">
      <div className="parent-progress-hero parent-notifications-hero">
        <div>
          <span className="parent-progress-eyebrow">School communication</span>
          <h1>School Notifications</h1>
          <p>
            Important announcements, calendar events and notices shared by the
            school.
          </p>
        </div>
        <div
          className="parent-progress-hero-stat"
          aria-label={`${notifications.length} notifications`}
        >
          <span>{notifications.length}</span>
          <small>Total notices</small>
        </div>
      </div>

      {loading ? (
        <PageState
          loading
          title="Loading notifications"
          text="Getting the latest updates from school."
        />
      ) : error ? (
        <PageState
          icon={ErrorOutline}
          error
          title="Notifications unavailable"
          text={error}
          onRetry={loadNotifications}
        />
      ) : (
        <>
          <div className="parent-notifications-toolbar">
            <label className="parent-notifications-search">
              <Search />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search notifications"
                aria-label="Search notifications"
              />
            </label>
            <label className="parent-notifications-filter">
              <span>Type</span>
              <select
                value={typeFilter}
                onChange={(event) => setTypeFilter(event.target.value)}
              >
                <option value="all">All notifications</option>
                {types.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="parent-notifications-heading">
            <div>
              <h2>Recent updates</h2>
              <p>
                Showing {visibleNotifications.length} of {notifications.length}{" "}
                notifications
              </p>
            </div>
          </div>
          {visibleNotifications.length ? (
            <div className="parent-notifications-list">
              {visibleNotifications.map((notification) => {
                const style = typeStyle(notification.notification_type?.name);
                const Icon = style.icon;
                return (
                  <article
                    className={`parent-notification-card tone-${style.tone}`}
                    key={notification.id}
                  >
                    <div className="parent-notification-icon">
                      <Icon />
                    </div>
                    <div className="parent-notification-content">
                      <div className="parent-notification-top">
                        <span className="parent-notification-type">
                          {notification.notification_type?.name || "General"}
                        </span>
                        <time>{formatDate(notification.date_time)}</time>
                      </div>
                      <h3>{notification.title || "School notification"}</h3>
                      <p>
                        {notification.description || "No message provided."}
                      </p>
                      {notification.start_date && (
                        <div className="parent-notification-dates">
                          <EventOutlined />
                          <span>
                            {formatDate(notification.start_date, false)}
                            {notification.end_date
                              ? ` — ${formatDate(notification.end_date, false)}`
                              : ""}
                          </span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => setSelectedNotification(notification)}
                      >
                        Read full message
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="parent-notifications-empty">
              <Search />
              <h2>No matching notifications</h2>
              <p>Try changing your search or notification type.</p>
            </div>
          )}
        </>
      )}

      {selectedNotification && (
        <NotificationDialog
          notification={selectedNotification}
          onClose={() => setSelectedNotification(null)}
        />
      )}
    </section>
  );
}

function NotificationDialog({ notification, onClose }) {
  const style = typeStyle(notification.notification_type?.name);
  const Icon = style.icon;
  return (
    <div
      className="parent-notification-modal-backdrop"
      role="presentation"
      onMouseDown={onClose}
    >
      <section
        className={`parent-notification-modal tone-${style.tone}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="notification-dialog-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div className="parent-notification-modal-icon">
            <Icon />
          </div>
          <div>
            <span>{notification.notification_type?.name || "General"}</span>
            <h2 id="notification-dialog-title">
              {notification.title || "School notification"}
            </h2>
            <time>{formatDate(notification.date_time)}</time>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close notification"
          >
            <Close />
          </button>
        </header>
        <div className="parent-notification-modal-body">
          <p>{notification.description || "No message provided."}</p>
          {notification.start_date && (
            <div className="parent-notification-modal-dates">
              <EventOutlined />
              <div>
                <span>Event period</span>
                <strong>
                  {formatDate(notification.start_date, false)}
                  {notification.end_date
                    ? ` — ${formatDate(notification.end_date, false)}`
                    : ""}
                </strong>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function PageState({ icon: Icon, title, text, loading, error, onRetry }) {
  return (
    <div
      className={`parent-notifications-state${error ? " error" : ""}`}
      role={error ? "alert" : "status"}
    >
      {loading ? (
        <span className="parent-notifications-spinner" />
      ) : (
        Icon && <Icon />
      )}
      <h2>{title}</h2>
      <p>{text}</p>
      {onRetry && (
        <button type="button" onClick={onRetry}>
          <Refresh /> Try again
        </button>
      )}
    </div>
  );
}
